/**
 * @file Contains logic for handling icons. Icons related to e.g. conveying information, signifying actions etc. are
 * within the scope of this file, but icons related to Secapp's functionality are not. For example, custom organization
 * icons are out of the scope of this file.
 */

import iconTemplateSource from './icon.tpl';

/* global Handlebars */

var
    /**
     * @type {!string}
     * The path to the directory containing the icons. The path is suitable for use from a client perspective, and might
     * not be suitable for accessing icons in code.
     */
    ICON_DIRECTORY_PATH = '/media/img/google_material_symbols',

    iconTemplate = Handlebars.compile(iconTemplateSource);

/**
 * Gets an HTML snippet for the specified icon. Note that this function should be used only when the server containing
 * the icon can be assumed to be reachable. For cases where the server might not be reachable, consider using
 * {@link getIconHtmlFromSvgData} instead.

 * @param {*} icon                                   The icon to get the HTML for. Must be one of the values of
 *                                                   {@link ICON}.
 * @param {?object} [options = null]                 Extra options to use for the icon or null to use the default
 *                                                   options
 * @param {?string} [options.tooltip = null]         The tooltip to display for the icon or null/an empty string for no
 *                                                   tooltip
 * @returns {!string} The HTML for the icon
 * @throws {!Error} Thrown if the given icon name is not supported
 */
export function getIconHtmlForServerIcon(icon, options) {
    var $icon;
    if (!options) {
        options = {};
    }
    $icon = $(iconTemplate({
        imageSource: getIconPath(icon),
        tooltip: options.tooltip
    }));
    return $icon.prop('outerHTML');
}

/**
 * Creates a data URI from SVG data with an optional color applied.
 * @param {!string} svgData                          The SVG element data that represents the icon
 * @param {?string} [color = null]                   The color to apply to the SVG (sets both stroke and fill).
 *                                                   If null, the SVG's original colors are preserved.
 * @returns {!string} The data URI for the SVG
 */
export function getSvgDataUri(svgData, color) {
    var $svg;

    if (color) {
        $svg = $(svgData);
        $svg.attr('stroke', color);
        $svg.attr('fill', color);
        svgData = $svg.prop('outerHTML');
    }

    return 'data:image/svg+xml;base64,' + btoa(svgData);
}

/**
 * Gets an HTML snippet for the given SVG data. This is useful when the icon needs to be usable even when the server
 * containing the icons cannot be reached. For cases where the server is reachable, consider using
 * {@link getIconHtmlForServerIcon} instead.
 * @param {!string} svgData                          The SVG element data that represents the icon
 * @param {?object} [options = null]                 Extra options to use for the icon or null to use the default
 *                                                   options
 * @param {?string} [options.color = null]           The color to apply to the SVG (sets both stroke and fill).
 *                                                   If null, the SVG's original colors are preserved.
 * @param {?string} [options.tooltip = null]         The tooltip to display for the icon or null/an empty string for no
 *                                                   tooltip
 * @returns {!string} The HTML for the icon
 */
export function getIconHtmlFromSvgData(svgData, options) {
    // To add new icons, download the icons files as instructed in the documentation of the ICON constant, then place
    // them in assets/icons/google-material-symbols.
    // Note: Unlike getIconHtml, this function doesn't use anything like the ICON constant. This is because if this file
    // contained the offline icons, every module using this module would also have to include data for all offline
    // icons.
    var $icon;

    if (!options) {
        options = {};
    }

    // Note: Technically an <svg> element could be used instead of a data URI, but it requires a notable amount of extra
    // CSS, which isn't nice for maintainability. Hence an <img> element with a data URI is used instead.
    $icon = $(iconTemplate({
        imageSource: getSvgDataUri(svgData, options.color),
        tooltip: options.tooltip
    }));
    return $icon.prop('outerHTML');
}

/**
 * Fetches an SVG icon and returns its HTML as a string with the specified color.
 *
 * @param {?string} icon                    Icon to fetch. Must be one of the values of {@link ICON}.
 * @param {!object} [options]               Options for the icon.
 * @param {!string} [options.color]         Stroke/fill color to apply to the SVG.
 * @returns {Promise<string>}               Resolves with the SVG HTML string if successful or rejects if an error
 *                                          occurs (e.g. loading the icon fails)
 * @throws {Error}                          If icon or color is missing.
 */
export function fetchIconSvgHtmlForServerIconWithColor(icon, options) {
    var svgUrl,
        iconOptions;

    if (!icon) {
        throw new Error('Icon must be provided.');
    }
    svgUrl = getIconPath(icon);

    if (!options || typeof options !== 'object') {
        throw new Error('Options object must be provided.');
    }

    if (!options.color) {
        throw new Error('Color must be provided in options.');
    }

    iconOptions = {
        color: options.color
    };

    return $.get(svgUrl)
        .then(function(svgData) {
            return getIconHtmlFromSvgData(svgData.documentElement.outerHTML, iconOptions);
        });
}

/**
 * Gets the path to the specified icon. The path will be suitable for use from a client perspective (e.g. as the src
 * property of an img element), and it might not be suitable for accessing the icon in code.
 * @param {*} icon The icon to get the path to. Must be one of the values of {@link ICON}.
 * @returns {!string} The path to the icon
 */
export function getIconPath(icon) {
    return ICON_DIRECTORY_PATH + '/' + icon;
}
